import {
  useState, useCallback, useRef, useEffect,
} from 'react';
import { useHistory } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { List } from 'immutable';
import { commerceCartAddProduct } from '@uncinc/react-drupal-core';
import {
  commerceLoadCart,
} from '@uncinc/react-drupal-core/dist/redux/actions/Commerce';

import useTypedDispatch from './useTypedDispatch';
import {
  cartHasDigitalProduct,
  cartHasSameDigitalProduct,
  clearCart,
  redirectToCheckout,
} from '../containers/Cart/Checkout/helpers/digitalProductHelpers';

// Global lock to prevent race conditions across multiple hook instances
// This ensures only one add-to-cart operation runs at a time, even if
// multiple components use useAddToCart simultaneously
let globalAddLock = false;

/**
 * Normalized product data that components pass to the hook.
 * Components are responsible for extracting this from their entity structure.
 */
export interface ProductData {
  /** UUID of the product variation to add */
  uuid: string;
  /** SKU for same-product detection */
  sku: string;
  /** Whether this is a digital product (e-learning or digital tool) */
  isDigitalProduct: boolean;
  /** Product title for analytics */
  title: string;
  /** Product price for analytics */
  price: number;
}

/**
 * Options for customizing add-to-cart behavior
 */
export interface AddToCartOptions {
  /** Skip automatic redirect after adding e-learning (default: false) */
  skipRedirect?: boolean;
  /** Skip analytics tracking (default: false) */
  skipAnalytics?: boolean;
  /** Callback after successful add */
  onSuccess?: () => void;
  /** Callback on error */
  onError?: (error: Error) => void;
}

interface AddToCartState {
  isLoading: boolean;
  error: Error | null;
}

interface UseAddToCartReturn extends AddToCartState {
  /**
   * Add product to cart with conflict handling.
   * - Clears cart if type conflict (digital vs physical)
   * - Clears cart if different digital product
   * - Redirects to checkout for digital products
   */
  addToCart: (
    product: ProductData,
    quantity: number,
    options?: AddToCartOptions
  ) => Promise<void>;
  /** Current cart products for UI display */
  cartProducts: List<unknown>;
}

// Delay after clearing cart before adding new product
// Prevents race condition where backend calculates total before removal completes
const CART_CLEAR_DELAY_MS = 500;

// Delay after adding product to allow cart reload to complete
// Prevents race condition where next add uses stale cart state
const CART_RELOAD_DELAY_MS = 300;

/**
 * Hook for adding products to cart with conflict handling.
 *
 * Handles:
 * - Cart conflict detection (e-learning vs physical products)
 * - Automatic cart clearing when switching product types
 * - Same e-learning product quantity addition
 * - Analytics tracking (add_to_cart event)
 * - Automatic redirect for e-learning products
 *
 * @example
 * ```tsx
 * const { addToCart, isLoading, cartProducts } = useAddToCart();
 *
 * const handleOrder = async () => {
 *   await addToCart({
 *     uuid: productUuid,
 *     sku: productSku,
 *     isDigitalProduct: false,
 *     title: 'Product Name',
 *     price: 29.99,
 *   }, quantity);
 * };
 * ```
 */
function useAddToCart(): UseAddToCartReturn {
  const dispatch = useTypedDispatch();
  const history = useHistory();

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const loggedIn = useSelector((state: any) => state.getIn(['login', 'loggedIn'], false));
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const cartProducts = useSelector((state: any) => state.getIn(['commerce', 'cart', 'products'], List()));

  // Use ref to always access fresh cart state in callbacks
  // This prevents stale closure issues where cartProducts captures old state
  const cartProductsRef = useRef(cartProducts);
  useEffect(() => {
    cartProductsRef.current = cartProducts;
  }, [cartProducts]);

  const [state, setState] = useState<AddToCartState>({
    isLoading: false,
    error: null,
  });

  const trackAddToCartAnalytics = useCallback(
    (product: ProductData, quantity: number) => {
      if (typeof window === 'undefined') return;

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (window as any).dataLayer = (window as any).dataLayer || [];
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (window as any).dataLayer.push({
        event: 'add_to_cart',
        productName: product.title,
        productPrice: product.price,
        productQuantity: quantity,
        productSku: product.sku,
        ecommerce: {
          currency: 'EUR',
          value: product.price * quantity,
          items: [
            {
              item_id: product.sku,
              item_name: product.title,
              price: product.price,
              quantity,
            },
          ],
        },
      });
    },
    [],
  );

  const addToCart = useCallback(
    async (
      product: ProductData,
      quantity: number,
      options: AddToCartOptions = {},
    ): Promise<void> => {
      const {
        skipRedirect = false,
        skipAnalytics = false,
        onSuccess,
        onError,
      } = options;

      // Prevent concurrent add operations across all hook instances
      // This fixes race conditions where rapid clicks bypass conflict checks
      if (globalAddLock) {
        return;
      }

      globalAddLock = true;
      setState({ isLoading: true, error: null });

      try {
        // Use ref to get fresh cart state (not stale closure value)
        const freshCartProducts = cartProductsRef.current;
        const hasProductsInCart = freshCartProducts.size > 0;

        // Check for cart conflicts
        if (hasProductsInCart) {
          const cartContainsDigitalProduct = cartHasDigitalProduct(freshCartProducts);
          const hasSameDigitalProduct = cartHasSameDigitalProduct(
            freshCartProducts,
            product.sku,
          );

          // If adding same digital product, just redirect (don't add duplicate)
          // Digital products are licenses, users can't buy multiple of the same
          if (product.isDigitalProduct && hasSameDigitalProduct) {
            if (!skipRedirect) {
              redirectToCheckout(loggedIn, history);
            }

            setState({ isLoading: false, error: null });
            globalAddLock = false;
            onSuccess?.();
            return;
          }

          // Check for type conflict or different digital product
          const hasTypeConflict = product.isDigitalProduct !== cartContainsDigitalProduct;
          const replacingWithDifferentDigitalProduct =
            product.isDigitalProduct && cartContainsDigitalProduct && !hasSameDigitalProduct;

          if (hasTypeConflict || replacingWithDifferentDigitalProduct) {
            // Clear cart and wait for backend to process
            await clearCart(dispatch, freshCartProducts);

            // Wait for cart reload to complete before adding new product
            await new Promise((resolve) => {
              setTimeout(resolve, CART_CLEAR_DELAY_MS);
            });
          }
        }

        // Add the product
        dispatch(commerceCartAddProduct(product.uuid, quantity));

        // Reload cart to ensure fresh prices
        dispatch(commerceLoadCart());

        // Wait for cart reload to complete before allowing new operations
        // This prevents race conditions where next add uses stale cart state
        await new Promise((resolve) => {
          setTimeout(resolve, CART_RELOAD_DELAY_MS);
        });

        // Track analytics
        if (!skipAnalytics) {
          trackAddToCartAnalytics(product, quantity);
        }

        // Redirect for digital products
        if (product.isDigitalProduct && !skipRedirect) {
          redirectToCheckout(loggedIn, history);
        }

        setState({ isLoading: false, error: null });
        onSuccess?.();
      } catch (error) {
        const err = error instanceof Error ? error : new Error(String(error));
        setState({ isLoading: false, error: err });
        onError?.(err);
      } finally {
        // Always release lock, even on error
        globalAddLock = false;
      }
    },
    [
      dispatch,
      history,
      loggedIn,
      trackAddToCartAnalytics,
    ],
  );

  return {
    addToCart,
    isLoading: state.isLoading,
    error: state.error,
    cartProducts,
  };
}

export default useAddToCart;
